import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../../../shared/components/ui/avatar';
import { Badge } from '../../../shared/components/ui/badge';
import { Card } from '../../../shared/components/ui/card';
import { ScrollArea } from '../../../shared/components/ui/scroll-area';
import { Spinner } from '../../../shared/components/Loading/Spinner';
import { useConversations } from '../hooks/useMessages';
import { Conversation } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { formatRoleLabel } from '../utils/formatRoleLabel';
import { getProfileUrl, hasClickableProfile } from '../utils/getProfileUrl';

type ConversationListProps = {
  selectedConversationId?: string;
  onSelectConversation: (conversation: Conversation) => void;
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatTimestamp = (date: Date): string => {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
};

const getRoleBadgeColor = (role: string): string => {
  switch (role) {
    case 'SERVICE_PROVIDER':
      return 'bg-[#026857] text-white';
    case 'FINANCIAL_ADVISOR':
      return 'bg-[#378F94] text-white';
    case 'CARE_SEEKER':
      return 'bg-gray-600 text-white';
    default:
      return 'bg-gray-400 text-white';
  }
};


type ConversationItemProps = {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
};

const ConversationItem = ({ conversation, isSelected, onClick }: ConversationItemProps) => {
  const hasUnread = conversation.unreadCount > 0;
  // "{Owner} & Team" once the participant runs a shared (delegated) inbox.
  const participantTitle = conversation.participant.isTeam
    ? `${conversation.participant.name} & Team`
    : conversation.participant.name;

  return (
    <Card
      className={`
        p-4 cursor-pointer transition-colors hover:bg-gray-50
        ${isSelected ? 'bg-[#026857]/5 border-[#026857]' : ''}
        ${hasUnread ? 'border-l-4 border-l-[#026857]' : ''}
      `}
      onClick={onClick}
      role="button"
      aria-label={`Conversation with ${conversation.participant.name}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Avatar className="h-12 w-12 flex-shrink-0">
          <AvatarImage
            src={conversation.participant.profilePhoto || undefined}
            alt={conversation.participant.name}
          />
          <AvatarFallback className="bg-[#026857] text-white">
            {getInitials(conversation.participant.name)}
          </AvatarFallback>
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name and Role */}
          <div className="flex items-center gap-2 mb-1 min-w-0">
            <h3
              className={`text-sm font-medium truncate flex-1 min-w-0 ${hasUnread ? 'font-semibold' : ''}`}
            >
              {hasClickableProfile(conversation.participant) ? (
                <Link
                  to={getProfileUrl(conversation.participant)!}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:text-[#026857] hover:underline transition-colors"
                >
                  {participantTitle}
                </Link>
              ) : (
                participantTitle
              )}
            </h3>
            <Badge
              className={`text-xs flex-shrink-0 ${getRoleBadgeColor(conversation.participant.role)}`}
            >
              {formatRoleLabel(conversation.participant)}
            </Badge>
          </div>

          {/* Last Message */}
          <p
            className={`text-sm text-gray-600 mb-1 line-clamp-2 break-words ${hasUnread ? 'font-medium text-gray-900' : ''}`}
          >
            {conversation.lastMessage.content.replace(/\r?\n/g, ' ')}
          </p>

          {/* Timestamp and Unread Count */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {formatTimestamp(conversation.lastMessage.timestamp)}
            </span>
            {hasUnread && (
              <Badge className="bg-[#026857] text-white h-5 min-w-[20px] px-1.5">
                {conversation.unreadCount}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const ConversationList = ({
  selectedConversationId,
  onSelectConversation,
}: ConversationListProps) => {
  const { data, isLoading, error } = useConversations(1, 50);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-6 text-center">
        <div>
          <p className="text-red-600 mb-2">Failed to load conversations</p>
          <p className="text-sm text-gray-600">Please try again later</p>
        </div>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-6 text-center">
        <div>
          <p className="text-gray-600 mb-2">No conversations yet</p>
          <p className="text-sm text-gray-500">
            Start a conversation by contacting a provider from the directory
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full [&>div>div]:!block">
      <div className="space-y-2 p-4">
        {data.data.map(conversation => (
          <ConversationItem
            key={conversation.conversationId}
            conversation={conversation}
            isSelected={selectedConversationId === conversation.conversationId}
            onClick={() => onSelectConversation(conversation)}
          />
        ))}
      </div>
    </ScrollArea>
  );
};

